/**
 * 最後のフレームのダイアログ (SceneLastFrameDialog) の寸法計算。
 *
 * ダイアログはスクロールさせず、カードの高さを画面に連動させる (CSS: .lastFrameDialogCard)。
 * 2 枚のフレーム (最初 / 最後) はフレーム行 (.lastFrameDialogFrames) の残り領域を ResizeObserver で測り、
 * ここで同じ寸法 (px) に決める。CSS の container query 単位 (cqh / cqw) でも書けるが、Chrome で
 * 比率やパネル幅の変更時に再評価されないことがあったため JS で確定させる。
 * 定数は ScenarioGenerator.module.css の --lf-* と一致させること。
 */

/** ラベル行の高さ (--lf-label-h) */
export const LAST_FRAME_DIALOG_LABEL_H = 24;
/** ラベル行とフレームの間隔 (--lf-col-gap) */
export const LAST_FRAME_DIALOG_COL_GAP = 8;
/** 横並びのときの列と矢印の間隔 (--lf-row-gap) */
export const LAST_FRAME_DIALOG_ROW_GAP = 18;
/** 縦並び (狭い画面) のときの間隔 */
export const LAST_FRAME_DIALOG_STACKED_GAP = 10;
/** 矢印の寸法 (--lf-arrow) */
export const LAST_FRAME_DIALOG_ARROW = 22;
/** 本文の左右余白 (--lf-body-pad) */
export const LAST_FRAME_DIALOG_BODY_PAD = 24;
/** カードの最小幅 (タイトル・フッターが収まる幅。CSS の max(520px, …) と同値) */
export const LAST_FRAME_DIALOG_MIN_CARD_W = 520;

/** '16:9' → 16/9。解釈できない値 (adaptive 等) は 16:9 扱い */
export function parseAspectRatioNumber(aspectRatio: string): number {
  const [w, h] = (aspectRatio || '').split(':').map(Number);
  if (!w || !h || !Number.isFinite(w) || !Number.isFinite(h)) return 16 / 9;
  return w / h;
}

export interface LastFrameDialogFrameSize {
  width: number;
  height: number;
}

/**
 * フレーム行の内側の寸法から、1 枚のフレームの寸法を決める。
 * 残りの高さから決めた幅と、残りの幅から決めた幅の小さい方 (比率は aspect = 幅 / 高さ)。
 * 1px 未満は切り捨て (小数で行からはみ出さないように)。
 */
export function computeLastFrameDialogFrameSize(
  rowWidth: number,
  rowHeight: number,
  aspect: number,
  stacked: boolean,
): LastFrameDialogFrameSize {
  const ar = aspect > 0 && Number.isFinite(aspect) ? aspect : 16 / 9;
  let availH: number;
  let availW: number;
  if (stacked) {
    availH = (rowHeight - 2 * (LAST_FRAME_DIALOG_LABEL_H + LAST_FRAME_DIALOG_COL_GAP) - 2 * LAST_FRAME_DIALOG_STACKED_GAP - LAST_FRAME_DIALOG_ARROW) / 2;
    availW = rowWidth;
  } else {
    availH = rowHeight - LAST_FRAME_DIALOG_LABEL_H - LAST_FRAME_DIALOG_COL_GAP;
    availW = (rowWidth - 2 * LAST_FRAME_DIALOG_ROW_GAP - LAST_FRAME_DIALOG_ARROW) / 2;
  }
  const width = Math.max(0, Math.floor(Math.min(availH * ar, availW)));
  const height = Math.max(0, Math.floor(width / ar));
  return { width, height };
}

/**
 * フレーム 1 枚の幅から、2 枚がぴったり収まるカードの幅を決める (横並びのみ)。
 * 高さで寸法が決まったとき、CSS の推定幅で生じる左右の余白を無くす。
 * 縦並び (狭い画面) ではカードは幕の幅いっぱい (CSS) なので null。
 * 幅は 2 × フレーム幅 + 間隔 2 つ + 矢印 + 左右余白。タイトル・フッターのため最小幅を下回らない。
 * 行の幅はカード幅 − 左右余白になるので、再計算しても同じフレーム幅に戻る (往復しない)
 */
export function computeLastFrameDialogCardWidth(frameWidth: number, stacked: boolean): number | null {
  if (stacked) return null;
  const fit = 2 * frameWidth + 2 * LAST_FRAME_DIALOG_ROW_GAP + LAST_FRAME_DIALOG_ARROW + 2 * LAST_FRAME_DIALOG_BODY_PAD;
  return Math.max(LAST_FRAME_DIALOG_MIN_CARD_W, Math.ceil(fit));
}
